<script setup lang="ts">
/**
 * SceneOnline · 在线小剧场：趴在窗台上的小家伙
 * ------------------------------------------------------------
 * 舞台 = 一扇白天的窗户：平时在窗内左右来回玩，偶尔探头把脑袋伸出
 * 窗框「看看你干活」（peek 由外壳驱动，探头动画与放大脑袋都在这里）。
 */
defineProps<{ peek?: boolean; hot?: boolean }>()
</script>

<template>
  <span class="sc" :class="{ peek, hot }" aria-hidden="true">
    <span class="win">
      <span class="kid">
        <svg viewBox="0 0 20 26">
          <defs>
            <linearGradient id="apShirtOn" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#7A95FF" />
              <stop offset="1" stop-color="#4C6BF0" />
            </linearGradient>
          </defs>
          <!-- 身体（连帽衫） -->
          <path d="M4.4 26v-5.4c0-2.7 2.4-4.3 5.6-4.3s5.6 1.6 5.6 4.3V26z" fill="url(#apShirtOn)" />
          <path d="M7.3 16.6q2.7 2.1 5.4 0" stroke="#FFFFFF" stroke-opacity=".55" stroke-width=".9"
                fill="none" stroke-linecap="round" />
          <!-- 手扶窗台 -->
          <rect x="1.1" y="21.3" width="4.5" height="3.3" rx="1.65" fill="#FFDCB4" stroke="#E6B183" stroke-width=".8" />
          <rect x="14.4" y="21.3" width="4.5" height="3.3" rx="1.65" fill="#FFDCB4" stroke="#E6B183" stroke-width=".8" />
          <!-- 头（整体一组，探头时成组放大） -->
          <g class="head">
            <circle cx="10" cy="8.4" r="6.6" fill="#FFDCB4" stroke="#E6B183" stroke-width=".9" />
            <!-- 头发 + 呆毛 -->
            <path d="M3.5 7.9a6.5 6.5 0 0 1 13 0v.8H3.5z" fill="#3A2B21" />
            <path d="M9.5 2.6c1.1-1.6 2.7-1.9 3.5-1.4-1.3.4-2.1 1-2.5 1.8z" fill="#3A2B21" />
            <!-- 眉 -->
            <path d="M6.9 5.9q1.1-.7 2.2-.2M13.1 5.9q-1.1-.7-2.2-.2" stroke="#3A2B21" stroke-width=".7"
                  fill="none" stroke-linecap="round" opacity=".75" />
            <!-- 眼（会眨） -->
            <ellipse cx="7.9" cy="8.6" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="3.4s" repeatCount="indefinite" />
            </ellipse>
            <ellipse cx="12.1" cy="8.6" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="3.4s" repeatCount="indefinite" />
            </ellipse>
            <circle cx="8.5" cy="7.9" r=".5" fill="#FFFFFF" opacity=".9" />
            <circle cx="12.7" cy="7.9" r=".5" fill="#FFFFFF" opacity=".9" />
            <!-- 腮红 -->
            <ellipse cx="5.5" cy="11" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <ellipse cx="14.5" cy="11" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <!-- 笑 -->
            <path d="M8.6 11.6a1.5 1.5 0 0 0 2.8 0z" fill="#C05A5A" />
          </g>
        </svg>
      </span>
      <span class="mull" />
    </span>
  </span>
</template>

<style scoped>
.sc { position: absolute; inset: 0; display: block; font-size: 0; }

/* 白天的窗：外框 + 一根竖框条；不裁剪，好让小人把脑袋探出框外 */
.win {
  position: absolute; left: 50%; bottom: 7px;
  width: 36px; height: 26px; margin-left: -18px;
  border: 2px solid #C3D3E8; border-radius: 8px;
  background:
    radial-gradient(circle at 76% 28%, #ffffff 0 2px, transparent 2.3px),
    radial-gradient(circle at 84% 40%, #ffffffb0 0 1.4px, transparent 1.7px),
    linear-gradient(180deg, #a9d6ff 0%, #dcf0ff 66%, #c4e3ff 100%);
  box-shadow: inset 0 -2px 3px #1a3a6614, 0 1px 2px #0b122012;
}
/* 竖框条：压在小人之上，小人经过时被它挡一下，像在窗里 */
.mull {
  position: absolute; z-index: 3;
  left: 50%; top: 0; width: 2px; height: 100%; margin-left: -1px;
  background: #c3d3e8;
}

/* 小人：窗内左右玩 → 蹲 → 蹦出框把脑袋探出来 */
.kid {
  position: absolute; left: 2px; bottom: 0; z-index: 2;
  display: block; font-size: 0; transform-origin: 50% 100%;
  filter: drop-shadow(0 1px 1px #1a3a6626);
  animation: apPlay 8s cubic-bezier(.45, .05, .55, .95) infinite;
}
.kid svg { display: block; width: 16px; height: 21px; }
/* 悬停时它更兴奋：玩得更快 */
.sc.hot .kid { animation-duration: 3.6s; }
@keyframes apPlay {
  0%   { transform: translate(0, 0) rotate(0deg) scaleY(1); }
  6%   { transform: translate(3px, -1px) rotate(-3deg) scaleY(1); }
  14%  { transform: translate(11px, -1px) rotate(2deg) scaleY(1); }
  20%  { transform: translate(11px, 1px) rotate(0deg) scaleY(.86); }
  26%  { transform: translate(11px, -8px) rotate(-2deg) scaleY(1.06); }
  33%  { transform: translate(11px, -7px) rotate(1deg) scaleY(1.02); }
  40%  { transform: translate(11px, 1px) rotate(0deg) scaleY(.9); }
  48%  { transform: translate(12px, -1px) rotate(3deg) scaleY(1); }
  56%  { transform: translate(5px, 1px) rotate(0deg) scaleY(.86); }
  62%  { transform: translate(4px, -8px) rotate(2deg) scaleY(1.06); }
  69%  { transform: translate(4px, -7px) rotate(-1deg) scaleY(1.02); }
  76%  { transform: translate(3px, 1px) rotate(0deg) scaleY(.9); }
  86%  { transform: translate(1px, -1px) rotate(-2deg) scaleY(1); }
  100% { transform: translate(0, 0) rotate(0deg) scaleY(1); }
}

/* 探头看你干活：蹲下蓄力 → 轻轻蹦出窗框一点 → 左右张望 → 缩回窗内 */
.sc.peek .kid { animation: apPeek 3.4s cubic-bezier(.45, .05, .55, .95) both; }
@keyframes apPeek {
  0%   { transform: translate(4px, 1px) rotate(0deg) scaleY(1); }
  8%   { transform: translate(4px, 2px) rotate(0deg) scaleY(.78); }
  22%  { transform: translate(4px, -12px) rotate(-3deg) scaleY(1.08); }
  34%  { transform: translate(5px, -11px) rotate(-4deg) scaleY(1.04); }
  50%  { transform: translate(6px, -11px) rotate(4deg) scaleY(1.04); }
  64%  { transform: translate(5px, -12px) rotate(-2deg) scaleY(1.05); }
  76%  { transform: translate(4px, -11px) rotate(0deg) scaleY(1.02); }
  88%  { transform: translate(4px, 2px) rotate(0deg) scaleY(.85); }
  100% { transform: translate(4px, 0) rotate(0deg) scaleY(1); }
}
/* 探头时脑袋成组放大（以脖子为支点），像特意把头伸出来看你 */
.head {
  transform-box: fill-box; transform-origin: 50% 100%;
  transition: transform .32s cubic-bezier(.3, 1.2, .4, 1);
}
.sc.peek .head { transform: scale(1.45) rotate(-2deg); transition-delay: .18s; }
</style>
